import { Box, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import PropTypes from "prop-types";
import { tokens } from "../theme";

const styles = {
  mainPaperProps: (colors, paperStyle) => ({
    elevation: 0,
    style: {
      backgroundColor: colors.primary[700],
      borderRadius: "10px",
      boxShadow: "0 16px 40px rgba(16, 24, 40, 0.14)",
      ...paperStyle,
    },
  }),
  confirmationPaperProps: (colors, paperStyle) => ({
    elevation: 0,
    style: {
      width: "520px",
      background: colors.surface,
      border: `1px solid ${colors.menuBorder}`,
      borderRadius: "10px",
      boxShadow: "0 12px 40px rgba(16, 24, 40, 0.22)",
      overflow: "hidden",
      ...paperStyle,
    },
  }),
  confirmationBackdrop: { backgroundColor: "rgba(16, 24, 40, 0.32)" },
  confirmationHeader: (colors) => ({
    padding: "13px 20px",
    background: colors.dialogHeaderBg,
    borderBottom: `1px solid ${colors.menuBorder}`,
  }),
  confirmationTitle: (colors) => ({
    font: "500 12.5px system-ui",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: colors.railMuted,
  }),
  confirmationContent: (colors) => ({
    padding: "22px 20px 24px",
    background: colors.surface,
  }),
  confirmationFooter: (colors) => ({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    borderTop: `1px solid ${colors.railRule}`,
    background: colors.drawerBody,
  }),
  confirmationButtonBase: (colors) => ({
    height: "34px",
    borderRadius: "6px",
    boxShadow: "none",
    minWidth: 0,
    font: "600 11.5px system-ui",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    "&:hover": { boxShadow: "none" },
  }),
  confirmationCancelButton: (colors) => ({
    padding: "0 15px",
    border: `1px solid ${colors.ctl}`,
    background: colors.surface,
    color: colors.ctlInk,
    "&:hover": {
      background: colors.ctlHoverBg,
      color: colors.ctlInkHover,
      border: `1px solid ${colors.ctl}`,
    },
  }),
  confirmationConfirmButton: (colors) => ({
    padding: "0 17px",
    background: colors.btn,
    border: `1px solid ${colors.btn}`,
    color: colors.surface,
    "&:hover": {
      background: colors.btnHover,
      border: `1px solid ${colors.btnHover}`,
    },
    "&.Mui-disabled": {
      background: colors.btnDisabledBg,
      border: `1px solid ${colors.btnDisabledBg}`,
      color: colors.surface,
    },
  }),
};

const ConvoyDialog = ({
  open,
  title,
  handleDialogConfirm,
  confirmBtnText,
  disabled,
  onCancelClick,
  cancelBtnText,
  resetDialog,
  children,
  additionalActions,
  paperStyle,
  variant,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  if (!open) {
    return <></>;
  }

  if (variant === "confirmation") {
    return (
      <Dialog
        open={open}
        onClose={resetDialog}
        PaperProps={styles.confirmationPaperProps(colors, paperStyle)}
        slotProps={{ backdrop: { sx: styles.confirmationBackdrop } }}
      >
        <Box sx={styles.confirmationHeader(colors)}>
          <Typography sx={styles.confirmationTitle(colors)}>{title}</Typography>
        </Box>
        <Box sx={styles.confirmationContent(colors)}>{children}</Box>
        <Box sx={styles.confirmationFooter(colors)}>
          {cancelBtnText?.trim() !== "" && (
            <Button
              variant="outlined"
              onClick={onCancelClick || resetDialog}
              sx={{
                ...styles.confirmationButtonBase(colors),
                ...styles.confirmationCancelButton(colors),
              }}
            >
              {cancelBtnText || "Cancel"}
            </Button>
          )}
          {additionalActions?.length &&
            additionalActions.map((action, i) => (
              <Button
                key={i}
                onClick={action?.onClick}
                sx={{
                  ...styles.confirmationButtonBase(colors),
                  ...styles.confirmationCancelButton(colors),
                }}
              >
                {action?.buttonText}
              </Button>
            ))}
          <Button
            variant="contained"
            onClick={handleDialogConfirm}
            disabled={disabled}
            autoFocus
            sx={{
              ...styles.confirmationButtonBase(colors),
              ...styles.confirmationConfirmButton(colors),
            }}
          >
            {confirmBtnText || "Confirm"}
          </Button>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={resetDialog}
      maxWidth="lg"
      PaperProps={styles.mainPaperProps(colors, paperStyle)}
    >
      <DialogTitle component="span">
        <Typography variant="h4">{title}</Typography>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        {cancelBtnText?.trim() !== "" && (
          <Button variant="outlined" onClick={onCancelClick || resetDialog}>
            <Typography variant="h6" color={colors.grey[100]} fontWeight={500}>
              {cancelBtnText || "Cancel"}
            </Typography>
          </Button>
        )}
        {additionalActions?.length &&
          additionalActions.map((action) => {
            return (
              <Button onClick={action?.onClick}>
                <Typography
                  variant="h6"
                  color={colors.grey[100]}
                  fontWeight={600}
                >
                  {action?.buttonText}
                </Typography>
              </Button>
            );
          })}
        <Button
          variant="contained"
          onClick={handleDialogConfirm}
          disabled={disabled}
          autoFocus
        >
          <Typography
            variant="h6"
            color={disabled ? colors.grey[400] : "#fff"}
            fontWeight={500}
          >
            {confirmBtnText || "Confirm"}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConvoyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  handleDialogConfirm: PropTypes.func,
  resetDialog: PropTypes.func.isRequired,
  confirmBtnText: PropTypes.string,
  cancelBtnText: PropTypes.string,
  paperStyle: PropTypes.object,
  variant: PropTypes.oneOf(["confirmation"]),
};

export default ConvoyDialog;
